/**
 * Utilitários para manipulação de contatos WhatsApp
 */

/**
 * Formata o JID do WhatsApp para exibição
 */
export const formatWhatsAppJid = (jid: string): string => {
  if (!jid) return 'N/A'
  
  // Remove sufixos do WhatsApp
  const cleanJid = jid.replace('@s.whatsapp.net', '').replace('@c.us', '').replace('@g.us', '')
  
  // Se for um número de telefone, formatar
  if (/^\d+$/.test(cleanJid)) {
    // Assumindo formato brasileiro: +55 (11) 99999-9999
    if (cleanJid.length >= 10) {
      const country = cleanJid.slice(0, 2)
      const area = cleanJid.slice(2, 4)
      const number = cleanJid.slice(4)
      
      if (number.length === 9) {
        return `+${country} (${area}) ${number.slice(0, 5)}-${number.slice(5)}`
      } else if (number.length === 8) {
        return `+${country} (${area}) ${number.slice(0, 4)}-${number.slice(4)}`
      }
    }
    
    return `+${cleanJid}`
  }
  
  return cleanJid
}

/**
 * Determina se o contato é um grupo
 */
export const isGroupContact = (jid: string): boolean => {
  return jid.endsWith('@g.us')
}

/**
 * Extrai o nome de exibição do contato
 */
export const getDisplayName = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
}): string => {
  if (contact.name) {
    return contact.name
  }

  if (contact.display_name) {
    return contact.display_name
  }

  if (contact.push_name_last) {
    return contact.push_name_last
  }

  // Fallback para o JID formatado
  return formatWhatsAppJid(contact.jid)
}

/**
 * Formata a data da última mensagem
 */
export const formatLastMessageDate = (lastMessageAt: string | null): string => {
  if (!lastMessageAt) {
    return 'Nunca'
  }

  const date = new Date(lastMessageAt)
  const now = new Date()

  // Compara dias do calendário (não diferença em horas) para evitar que
  // mensagens enviadas ontem apareçam como "Hoje" quando faz menos de 24h.
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

  const msPerDay = 1000 * 60 * 60 * 24
  const diffInDays = Math.floor((startOfDay(now) - startOfDay(date)) / msPerDay)

  if (diffInDays <= 0) {
    return 'Hoje'
  } else if (diffInDays === 1) {
    return 'Ontem'
  } else if (diffInDays < 7) {
    return `${diffInDays} dias atrás`
  } else {
    return date.toLocaleDateString('pt-BR')
  }
}

/**
 * Obtém o telefone formatado do contato
 * Para exportação: retorna "none" se não houver nome
 */
export const getContactPhone = (contact: {
  phone?: string | null
  jid: string
}): string => {
  if (contact.phone) {
    return contact.phone
  }

  return formatWhatsAppJid(contact.jid)
}

/**
 * Obtém o telefone formatado para exportação
 * Retorna "none" se o contato não tiver nome definido
 */
export const getContactPhoneForExport = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  phone?: string | null
  jid: string
}): string => {
  // Verifica se o contato tem algum nome definido
  const hasName = contact.name || contact.display_name || contact.push_name_last

  // Se não tiver nome, retorna "none"
  if (!hasName) {
    return 'none'
  }

  // Se tiver nome, retorna o telefone normalmente
  if (contact.phone) {
    return contact.phone
  }

  return formatWhatsAppJid(contact.jid)
}

/**
 * Gera URL de avatar baseado em profile picture ou iniciais
 * Tenta usar a foto real se disponível, senão retorna SVG com iniciais
 */
export const getContactAvatarUrl = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
  profile_picture?: string | null
}): string => {
  // Se tiver profile_picture, usar ela
  if (contact.profile_picture) {
    return contact.profile_picture
  }

  // Senão, gerar SVG com iniciais
  return generateContactAvatar(contact)
}

/**
 * Gera avatar baseado no nome ou JID
 */
export const generateContactAvatar = (contact: {
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  jid: string
}): string => {
  const name = getDisplayName(contact)
  const asciiLetterRegex = /[A-Za-z0-9\u00C0-\u024F]/
  const rawInitials = name
    .split(' ')
    .map(word => {
      for (const char of word) {
        if (asciiLetterRegex.test(char)) return char
      }
      return ''
    })
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const initials = rawInitials || (contact.jid ? contact.jid.replace(/\D/g, '').slice(-2) || '?' : '?')

  const backgroundColor = '#a1a1aa'
  const textColor = '#000000'

  const svg = `<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="${backgroundColor}"/><text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">${initials}</text></svg>`

  try {
    return `data:image/svg+xml,${encodeURIComponent(svg)}`
  } catch {
    const fallbackSvg = `<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="20" fill="${backgroundColor}"/><text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">?</text></svg>`
    return `data:image/svg+xml,${encodeURIComponent(fallbackSvg)}`
  }
}

/**
 * Determina se o contato tem mensagens não lidas, comparando
 * `last_message_at` com `last_read_at`. A regra é: existe mensagem
 * (last_message_at definido) e o último "read" é nulo OU é anterior.
 */
export const isContactUnread = (contact: {
  last_message_at?: string | null
  last_read_at?: string | null
}): boolean => {
  if (!contact.last_message_at) return false
  if (!contact.last_read_at) return true
  return new Date(contact.last_read_at).getTime() < new Date(contact.last_message_at).getTime()
}

/**
 * Formata a data de cadastro do contato
 */
export const formatCreatedDate = (createdAt: string | null): string => {
  if (!createdAt) {
    return 'Data desconhecida'
  }

  const date = new Date(createdAt)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Valida se uma string é um UUID válido
 */
export const isValidUUID = (uuid: string): boolean => {
  if (!uuid || typeof uuid !== 'string') {
    return false
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

/**
 * Formata telefone internacional com código do país
 */
export const formatInternationalPhone = (phone: string, countryCode: string = '55'): string => {
  // Remove caracteres não numéricos
  const cleanPhone = phone.replace(/\D/g, '')

  // Se já começa com o código do país, retorna como está
  if (cleanPhone.startsWith(countryCode)) {
    return cleanPhone
  }

  // Adiciona o código do país
  return `${countryCode}${cleanPhone}`
}

/** Contato individual com mesmo número pode vir em mais de uma linha (JID @c.us vs @s.whatsapp.net, etc.). */
export type ContactDedupeFields = {
  id: string
  jid: string
  phone?: string | null
  is_group?: boolean | null
  name?: string | null
  display_name?: string | null
  push_name_last?: string | null
  last_message_at?: string | null
}

function digitsForCanonicalMatch(contact: Pick<ContactDedupeFields, 'jid' | 'phone'>): string | null {
  const jid = contact.jid || ''
  if (jid.endsWith('@g.us')) return null
  if (jid.includes('@lid')) return null

  const fromJid = jid.replace(/\D/g, '')
  if (fromJid.length >= 8) return fromJid

  const fromPhone = (contact.phone || '').replace(/\D/g, '')
  if (fromPhone.length >= 8) return fromPhone

  return null
}

/**
 * Chave estável para agrupar o mesmo número WhatsApp entre formatos de JID diferentes.
 */
export function getContactCanonicalKey(contact: Pick<ContactDedupeFields, 'jid' | 'phone' | 'is_group'>): string {
  const jid = contact.jid || ''
  if (contact.is_group || jid.endsWith('@g.us')) {
    return `group:${jid}`
  }
  if (jid.includes('@lid')) {
    return `lid:${jid}`
  }
  const digits = digitsForCanonicalMatch(contact)
  if (digits) return `pn:${digits}`
  return `jid:${jid}`
}

/**
 * Variações de `phone` e `jid` possivelmente gravadas no banco para o mesmo número (BR: com/sem 55, 9 da operadora, @s.whatsapp.net vs @c.us).
 * Usado antes de inserir contato para evitar duplicata.
 */
export function buildContactDuplicateLookupVariants(
  contact: Pick<ContactDedupeFields, 'jid' | 'phone' | 'is_group'>
): { phones: string[]; jids: string[] } {
  const jid = (contact.jid || '').trim()
  const key = getContactCanonicalKey(contact)

  if (contact.is_group || jid.endsWith('@g.us')) {
    return jid ? { phones: [], jids: [jid] } : { phones: [], jids: [] }
  }
  if (jid.includes('@lid')) {
    return jid ? { phones: [], jids: [jid] } : { phones: [], jids: [] }
  }
  if (key.startsWith('jid:')) {
    const j = key.slice(4)
    return j ? { phones: [], jids: [j] } : { phones: [], jids: [] }
  }
  if (!key.startsWith('pn:')) {
    return jid ? { phones: [], jids: [jid] } : { phones: [], jids: [] }
  }

  const D = key.slice(3)
  const phones = expandPhoneDigitVariantsForLookup(D)
  const jids = new Set<string>()
  for (const p of phones) {
    if (p.length >= 8) {
      jids.add(`${p}@s.whatsapp.net`)
      jids.add(`${p}@c.us`)
    }
  }
  if (jid) jids.add(jid)

  return {
    phones: [...phones].filter((p) => p.length >= 8),
    jids: [...jids],
  }
}

function expandPhoneDigitVariantsForLookup(D: string): Set<string> {
  const out = new Set<string>()
  if (!D || !/^\d{8,}$/.test(D)) {
    if (D) out.add(D)
    return out
  }
  out.add(D)

  if (D.startsWith('55') && D.length >= 12) {
    const rest = D.slice(2)
    out.add(rest)
    if (rest.length === 11 && rest[2] === '9') {
      out.add(rest.slice(0, 2) + rest.slice(3))
    }
    if (rest.length === 10) {
      out.add(rest.slice(0, 2) + '9' + rest.slice(2))
    }
    return out
  }

  if ((D.length === 10 || D.length === 11) && !D.startsWith('55')) {
    out.add(`55${D}`)
    if (D.length === 11 && D[2] === '9') {
      out.add(`55${D.slice(0, 2)}${D.slice(3)}`)
    }
    if (D.length === 10) {
      out.add(`55${D.slice(0, 2)}9${D.slice(2)}`)
    }
  }

  return out
}

function contactQualityScore(contact: ContactDedupeFields): number {
  let s = 0
  const t = contact.last_message_at ? new Date(contact.last_message_at).getTime() : 0
  s += t / 1e15
  if (contact.name || contact.display_name || contact.push_name_last) s += 10
  if (contact.jid.endsWith('@s.whatsapp.net')) s += 0.5
  return s
}

function pickBetterDuplicate<T extends ContactDedupeFields>(a: T, b: T): T {
  const da = contactQualityScore(a)
  const db = contactQualityScore(b)
  if (Math.abs(db - da) > 1e-9) return db >= da ? b : a
  return a.id.localeCompare(b.id) <= 0 ? a : b
}

/**
 * Remove duplicatas visuais (mesmo número / mesmo grupo), mantendo o registro “melhor”
 * (última mensagem mais recente, nome disponível, JID @s.whatsapp.net).
 */
export function dedupeAccountContacts<T extends ContactDedupeFields>(contacts: T[]): T[] {
  const byKey = new Map<string, T>()
  for (const c of contacts) {
    const key = getContactCanonicalKey(c)
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, c)
      continue
    }
    byKey.set(key, pickBetterDuplicate(existing, c))
  }
  return Array.from(byKey.values()).sort((a, b) => {
    const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : 0
    const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : 0
    return bTime - aTime
  })
}